import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import validator from 'email-validator';
import SessionStore from 'stores/SessionStore';
import { getDeviceHash } from 'utils/browser';
import { Button } from 'react-bootstrap';
import Alert from 'components/ui/Alert';
import Table from 'components/v2/Table';
import Label from 'components/v2/forms/Label';
import TextField from 'components/v2/forms/TextField';
import ExternalLink from 'components/v2/ExternalLink';
import DocumentsWebAPIUtils from 'utils/DocumentsWebAPIUtils';
import LoadingCircle from 'components/ui/LoadingCircle';
import CargomaticCard from 'components/v2/card';
import AccountingWebAPIUtils from 'utils/AccountingWebAPIUtils';
import Pagination from 'components/v2/PaginationV2';
import { showToast } from 'stores/ToastStore';

const TOO_MANY_DOCUMENTS = 'TOO_MANY_DOCUMENTS';
const NO_DOCUMENTS_FOUND_ERROR = 'NO_DOCUMENTS_FOUND';
const DOCUMENTS_PER_PAGE = 20;

export default function PayoutResults({ payoutNumbers, handleCancel }) {
  const [payouts, setPayouts] = useState([]);
  const [searching, setSearching] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [emails, setEmails] = useState();
  const [emailsInvalid, setEmailsInvalid] = useState(false);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    (async () => {
      setPayouts([]);
      setSearching(true);
      setNoResults(false);

      try {
        const {
          entity: {
            data: payouts,
            meta: { count },
          },
        } = await AccountingWebAPIUtils.getPayoutsByNumbers(
          payoutNumbers,
          page,
          DOCUMENTS_PER_PAGE,
        );

        payouts.forEach((payout) => {
          payout.id = payout._id;
        });

        setPayouts(payouts);
        setTotalPages(Math.ceil(count / DOCUMENTS_PER_PAGE));

        if (payouts.length === 0) {
          setNoResults(true);
        }

        setSearching(false);
      } catch (e) {
        console.log('Error', e.body);

        setSearching(false);
        setNoResults(true);
      }
    })();
  }, [payoutNumbers, page]);

  const headers = [
    {
      field: 'number',
      label: t('PAYOUT_NUMBER'),
    },
    {
      field: 'carrier',
      label: t('CARRIER'),
    },
  ];

  const columns = [
    {
      field: 'number',
      render: (payoutNumber, row) => {
        return (
          <ExternalLink
            href={`/payouts/${row._id}`}
            title={payoutNumber}
            showIcon
          />
        );
      },
    },
    {
      field: 'carrier',
      render: (carrier, row) => {
        return (
          <ExternalLink
            href={`/users/${carrier}`}
            title={row.carrier_name}
            showIcon
          />
        );
      },
    },
  ];

  const handleEmailsChange = (value) => {
    const emails = value.replace(/' '/g, '').split(',');
    const invalidEmailAddresses = emails.filter(
      (ea) => !validator.validate(ea),
    );
    if (invalidEmailAddresses?.length > 0) {
      setEmailsInvalid(true);
    } else {
      setEmailsInvalid(false);
    }
    setEmails(value);
  };

  const handleDownload = async () => {
    try {
      setLoading(true);

      const deviceHash = getDeviceHash();
      const languageCodeId =
        SessionStore.getLanguageCodeIdPreference(deviceHash) || 'en-us';

      await DocumentsWebAPIUtils.bulkDownloadPayoutsByNumbers(
        payoutNumbers,
        emails,
        languageCodeId,
      );

      showToast(t('DOCUMENTS_SENT_VIA_EMAIL'), null, 'success');
    } catch (e) {
      if (e.entity?.error === NO_DOCUMENTS_FOUND_ERROR) {
        showToast(
          t('DOWNLOAD_DOCUMENTS_ERROR'),
          t('NO_DOCUMENTS_ARE_FOUND_FOR_DOWNLOAD'),
          'error',
        );
      } else if (e.entity?.error === TOO_MANY_DOCUMENTS) {
        showToast(
          t('DOWNLOAD_DOCUMENTS_ERROR'),
          t('TOO_MANY_DOCUMENTS_PLEASE_FORMULATE_A_MORE_PRECISE_REQUEST'),
          'error',
        );
      } else {
        showToast(
          t('DOWNLOAD_DOCUMENTS_ERROR'),
          t('DOWNLOAD_DOCUMENTS_ERROR'),
          'error',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h3 className="mb-4">{t('SELECTED_PAYOUTS')}</h3>
      <div className="overflow-x-auto table-wrapper max-w-xl">
        <Table headers={headers} columns={columns} data={payouts} />
        {searching && (
          <div
            className="mt-4 pb-2 flex flex-col items-center"
            data-testid="searchingMessage"
          >
            <LoadingCircle color="white" text={t('LOADING')} />
          </div>
        )}
        {noResults && (
          <div className="mt-4 pb-2 text-center" data-testid="noResultsMessage">
            {t('NO_RESULTS_TO_DISPLAY')}
          </div>
        )}
        {totalPages > 1 && (
          <Pagination
            page={page}
            pages={totalPages}
            perPage={DOCUMENTS_PER_PAGE}
            onPageClick={setPage}
          />
        )}
      </div>
      <CargomaticCard className="mt-4 max-w-xl">
        <div data-testid="downloadForm">
          <h3 className="mt-0">{t('EMAIL_NOTIFICATIONS')}</h3>

          <div className="mt-4 sm:w-80 w-full">
            <Label htmlFor="emails" className="">
              {t('EMAILS')}
            </Label>
            {emailsInvalid && (
              <Alert type="warning">{t('VALID_EMAIL_ONLY')}</Alert>
            )}
            <TextField
              type="text"
              name="emails"
              id="emails"
              value={emails || ''}
              placeholder={t('ENTER_EMAILS_SEPARATED_BY_COMMAS')}
              disabled={loading}
              onChange={handleEmailsChange}
            />
          </div>
          <div className="sm:flex sm:justify-end sm:space-x-4 mt-8 flex-wrap">
            {loading && (
              <div data-testid="loadingMessage">
                <LoadingCircle color="white" text={t('LOADING')} />
              </div>
            )}
            <Button
              data-testid="downloadButton"
              bsStyle="green"
              className="mb-4 sm:mb-0"
              onClick={handleDownload}
              disabled={
                noResults || searching || loading || !emails || emailsInvalid
              }
            >
              {t('EMAIL_PAYOUTS')}
            </Button>
            <Button
              data-testid="cancelButton"
              onClick={handleCancel}
              disabled={loading}
            >
              {t('CANCEL')}
            </Button>
          </div>
        </div>
      </CargomaticCard>
    </>
  );
}

PayoutResults.propTypes = {
  payoutNumbers: PropTypes.arrayOf(PropTypes.string),
  handleCancel: PropTypes.func,
};
